import React, { useState, useEffect } from "react";
import { Formik, Form, FormikHelpers, Field } from "formik";
import * as Yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../Appstore/store";
import {
  Updateagentmonitors,
  ViewAgentMonitor,
} from "../Features/Getallagentsmonitors";
import { IoMdArrowRoundBack } from "react-icons/io";
import { setIsLoading } from "../Features/loadingSlice";
import { BiHide, BiShow } from "react-icons/bi";
import { toast } from "sonner";

interface FormValues {
  name?: string;
  email?: string;
  phoneNumber?: string;
  agentmonitors?: string;
  password?: string;
  confirmPassword?: string;
  status?: string;
}

const Editrecord: React.FC = () => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { id }: any = useParams();

  const { agentmonitordata } = useSelector(
    (state: RootState) => state.agentmonitor
  );
  const record: FormValues | any = agentmonitordata.find(
    (recodID) => recodID.id === Number(id)
  );

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(3, "Name must be at least 3 characters")
      .max(250, "Name must be at most 250 characters")
      .required("Name is required")
      .trim(),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phoneNumber: Yup.string()
      .matches(
        /^\+1\d{10}$/,
        "Invalid phone number format. Must start with +1 and contain 10 digits without any spaces."
      ),
    agentmonitors: Yup.string().required("Please select an option"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/\d/, "Password must contain at least one number"),
    confirmPassword: Yup.string().oneOf(
      [Yup.ref("password")],
      "Passwords do not match"
    ),
    status: Yup.string().required("Status is required"),
  });

  useEffect(() => {
    if (id) {
      dispatch(setIsLoading(true));
      dispatch(ViewAgentMonitor(id))
        .unwrap()
        .catch((err) => {
          console.error("Error fetching agent monitor:", err);
        })
        .finally(() => {
          setTimeout(() => dispatch(setIsLoading(false)), 500);
        });
    }
  }, [id, dispatch]);

  const initialValues: FormValues = {
    name: record?.name || "",
    email: record?.email || "",
    phoneNumber: record?.phone_number || "",
    agentmonitors: record?.role === 1 ? "agents" : "monitors",
    password: "",
    confirmPassword: "",
    status: record?.status === 1 ? "Active" : "Deactive",
  };

  const navigatetobackpage = () => {
    navigate(-1);
  };

  return (
    <div>
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content">
            <div className="container-fluid">
              <div className="card mt-4">
                <div className="card-header bg-white p-3 d-flex justify-content-md-between">
                  <h2 className="w-100 mb-md-0 fs-3">Update User</h2>
                  <Link to={""} className="text-decoration-none">
                    <button
                      onClick={navigatetobackpage}
                      className="btn btn-info text-white d-flex align-items-center fw-semibold"
                    >
                      <IoMdArrowRoundBack /> Back
                    </button>
                  </Link>
                </div>
                <div className="card-body">
                  <Formik
                    initialValues={initialValues}
                    enableReinitialize={true}
                    validationSchema={validationSchema}
                    onSubmit={async (
                      values: FormValues,
                      { setSubmitting }: FormikHelpers<FormValues>
                    ) => {
                      const { id }: any = record;
                      const agentmonitordata = {
                        name: values.name,
                        email: values.email,
                        phone_number: values.phoneNumber,
                        status: values.status === "Active" ? 1 : 0,
                        role: values.agentmonitors === "agents" ? 1 : 2,
                        password: values.password,
                        confirm_password: values.confirmPassword,
                      };
                      try {
                        const response: any = await dispatch(
                          Updateagentmonitors({
                            id: id ?? 0,
                            updatedData: agentmonitordata,
                          })
                        );
                        if (
                          response.payload &&
                          response.payload.status === "success"
                        ) {
                          navigate("/admin/users");
                          toast.success(response.payload.message);
                        } else {
                          toast.error(response.payload.message);
                        }
                      } finally {
                        setSubmitting(false);
                        dispatch(setIsLoading(false));
                      }
                    }}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      isSubmitting,
                    }) => (
                      <Form>
                        <div className="row">
                          <div className="form-group mb-3 col-md-6">
                            <label htmlFor="name">
                              Name<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control shadow-none ${touched.name && errors.name ? "is-invalid" : ""
                                }`}
                              id="name"
                              name="name"
                              placeholder="Enter a Name"
                              value={values.name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {touched.name && errors.name && (
                              <div className="text-danger">{errors.name}</div>
                            )}
                          </div>

                          <div className="form-group mb-3 col-md-6">
                            <label>
                              Role<span className="text-danger">*</span>
                            </label>
                            <select
                              name="agentmonitors"
                              className="form-select shadow-none"
                              value={values.agentmonitors}
                              onChange={handleChange}
                            >
                              <option value="agents">Agents</option>
                              <option value="monitors">Monitors</option>
                            </select>
                            {touched.agentmonitors && errors.agentmonitors && (
                              <div className="text-danger">
                                {errors.agentmonitors}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="row">
                          <div className="form-group mb-3 col-md-6">
                            <label htmlFor="email">
                              Email<span className="text-danger">*</span>
                            </label>
                            <input
                              type="email"
                              className={`form-control shadow-none ${touched.email && errors.email
                                ? "is-invalid"
                                : ""
                                }`}
                              id="email"
                              name="email"
                              placeholder="Enter Email"
                              value={values.email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {touched.email && errors.email && (
                              <div className="text-danger">
                                {Array.isArray(errors.email)
                                  ? errors.email.join(", ")
                                  : String(errors.email)}
                              </div>
                            )}
                          </div>

                          <div className="form-group mb-3 col-md-6">
                            <label htmlFor="phoneNumber">
                              Phone Number<span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control shadow-none ${touched.phoneNumber && errors.phoneNumber
                                ? "is-invalid"
                                : ""
                                }`}
                              id="phoneNumber"
                              name="phoneNumber"
                              placeholder="Enter Phone Number"
                              value={values.phoneNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {touched.phoneNumber && errors.phoneNumber && (
                              <div className="text-danger">
                                {Array.isArray(errors.phoneNumber)
                                  ? errors.phoneNumber.join(", ")
                                  : String(errors.phoneNumber)}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="row">
                          <div className="form-group mb-3 col-md-6">
                            <label htmlFor="password">Password</label>
                            <div className="position-relative">
                              <Field
                                type={showPassword ? "text" : "password"}
                                name="password"
                                className={`form-control shadow-none ${touched.password && errors.password
                                  ? "is-invalid"
                                  : ""
                                  }`}
                                placeholder="Enter Password (optional)"
                              />
                              <button
                                type="button"
                                className="btn btn-light position-absolute"
                                onClick={() => setShowPassword(!showPassword)}
                                style={{
                                  top: "50%",
                                  right: "10px",
                                  transform: "translateY(-50%)",
                                  border: "none",
                                  background: "none",
                                }}
                              >
                                {showPassword ? <BiShow /> : <BiHide />}
                              </button>
                            </div>
                            {touched.password && errors.password && (
                              <div className="text-danger">
                                {Array.isArray(errors.password)
                                  ? errors.password.join(", ")
                                  : String(errors.password)}
                              </div>
                            )}
                          </div>

                          <div className="form-group mb-3 col-md-6">
                            <label htmlFor="confirmPassword">
                              Confirm Password
                            </label>
                            <div className="position-relative">
                              <Field
                                type={showConfirmPassword ? "text" : "password"}
                                name="confirmPassword"
                                className={`form-control shadow-none ${touched.confirmPassword &&
                                  errors.confirmPassword
                                  ? "is-invalid"
                                  : ""
                                  }`}
                                placeholder="Confirm Password (optional)"
                              />
                              <button
                                type="button"
                                className="btn btn-light position-absolute"
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                                style={{
                                  top: "50%",
                                  right: "10px",
                                  transform: "translateY(-50%)",
                                  border: "none",
                                  background: "none",
                                }}
                              >
                                {showConfirmPassword ? <BiShow /> : <BiHide />}
                              </button>
                            </div>
                            {touched.confirmPassword &&
                              errors.confirmPassword && (
                                <div className="text-danger">
                                  {errors.confirmPassword}
                                </div>
                              )}
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label>
                            Status<span className="text-danger">*</span>
                          </label>
                          <div className="d-flex align-items-center">
                            <input
                              type="radio"
                              id="active"
                              name="status"
                              value="Active"
                              checked={values.status === "Active"}
                              onChange={handleChange}
                            />
                            <label htmlFor="active" className="ms-1 mb-0">
                              Active
                            </label>

                            <input
                              type="radio"
                              id="deactive"
                              name="status"
                              value="Deactive"
                              checked={values.status === "Deactive"}
                              onChange={handleChange}
                              className="ms-3"
                            />
                            <label htmlFor="deactive" className="ms-1 mb-0">
                              Deactive
                            </label>
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? "Save..." : "Save"}
                          </button>
                          <Link to="/admin/users" className="btn btn-secondary">
                            Cancel
                          </Link>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Editrecord;
